import {
  totalSumText,
  LocalStorageExists,
  addRowsFromLocalStorageWhenLoggedIn,
  parseFormattedPrice,
  parseNumberOfProducts,
  formattedNr,
  readLocalWishlist,
  saveLocalWishlist,
} from './muun-wl-helpers.js';
import { updateWishlist } from './muun-WL-state.js';
import { fetchProductDetails } from './muun-WL-fetchProductDetails.js';
import { getWishlist } from './muun-WL-getWishlist.js';
import { newRow } from './muun-WL-favoritesPageAddNewRow.js';
import { addWlElemToCart, addWlElemToCartGrid } from './muun-WL-addToCart.js';
import { postWishlist } from './muun-WL-postWishlist.js';
import { addAllWlElemsToCart } from './muun-WL-addAllToCart.js';
import { showListView, showGridView } from './muun-WL-toggle-list-grid.js';

class WishlistOverview extends HTMLElement {
  constructor() {
    super();
    this.isLoggedIn = this.getAttribute('is-logged-in') === 'true';
    this.customerId = this.getAttribute('customer-id');
    this.shopDomain = this.getAttribute('shop-domain');
    this.quantityDecreaseText = this.getAttribute('quantity-decrease-text');
    this.quantityIncreaseText = this.getAttribute('quantity-increase-text');
    this.quantityInputText = this.getAttribute('quantity-input-text');
    this.addToCartText = this.getAttribute('add-to-cart-text');
    this.unavailableText = this.getAttribute('not-available-text');
    this.totalAmount = 0.0;
    this.gridView = true;
  }
  async connectedCallback() {
    // VARIABLES
    this.numberDisplay = this.querySelector('#numberOfProductsElement');
    this.favContainer = this.querySelector('#favoritesContainer');
    this.totalSumElem = this.querySelector('#totalSum');
    this.belowTableContainer = this.querySelector('#below-table-container');
    this.addButton = this.querySelector('#addLocalFavorites');
    this.localFavElement = this.querySelector('#addLocalFavoritesElement');
    this.modal = this.querySelector('#custom-modal');
    const cartItems = document.getElementsByClassName('wl-element');
    // CHECK FOR WISHLIST DATA
    let wishlistData = await updateWishlist(this.isLoggedIn, this.customerId, this.shopDomain);
    // CHECK IF ADD TO USER ACCOUNT SHOULD BE VISIBLE
    if (this.isLoggedIn) {
      if (LocalStorageExists()) {
        this.addButton.style.display = 'block';
        this.localFavElement.style.display = 'block';
      }
    }
    // SET THE NUMBER OF PRODUCTS (ONLY VISIBLE ON MOBILE)
    this.numberOfElements = wishlistData.length;
    this.numberDisplay.innerHTML = parseNumberOfProducts(this.numberOfElements);
    // GRAPH QL QUERY TO DISPLAY ITEMS FROM PRODUCT IDs
    let prodDetails = await fetchProductDetails(wishlistData, this.shopDomain);
    // CREATE THE TABLE ROWS
    prodDetails.forEach((product) => {
      const hasMultipleVariants = product.variants.length > 1;
      let is_available = hasMultipleVariants
        ? product.variants?.some(variant => variant.availability === true)
        : product.variants?.[0]?.availability === true;
      const insertedRow = newRow({
        is_available: is_available,
        productId: product.id,
        productHandle: product.handle,
        productTitle: product.title,
        productPriceAmount: product.variants[0].priceAmount,
        productcomparePrice: product.variants[0].compareAtPriceAmount,
        productCurrency: product.variants[0].priceCurrency,
        imgUrl: product.featuredImage?.url,
        imgAlt: product.featuredImage?.alt || '',
        decText: this.quantityDecreaseText,
        incText: this.quantityIncreaseText,
        inputText: this.quantityInputText,
        gridView: this.gridView,
        hasMultipleVariants: hasMultipleVariants,
        variants: product.variants,
        productAmount: product.amount,
        cartButtonText: this.addToCartText,
        cartUnavailable: this.unavailableText,
      });
      this.favContainer.appendChild(insertedRow);
      if (insertedRow && typeof insertedRow.form === 'undefined') {
        // Forcefully re-instantiate the custom element logic
        customElements.upgrade(insertedRow);
      }
      // Counter for the total sum
      const availableVariant = product.variants.find(v => v.availability === true);
      if (availableVariant) {
        this.totalAmount += parseFloat(availableVariant.priceAmount * product.amount);
      }
      // ADD TO CART FUNCTIONALITY
      insertedRow.dataset.variantId = product.variants[0].id;
      addWlElemToCart(insertedRow, product.variants[0].id, product.id);
      addWlElemToCartGrid(insertedRow, product.variants[0].id, product.id);
    });
    // TOGGLE LIST AND GRID VIEW
    this.querySelector('#wl-list-view').addEventListener('click', () => (showListView(), (this.gridView = false)));
    this.querySelector('#wl-grid-view').addEventListener('click', () => (showGridView(), (this.gridView = true)));
    // DISPLAY THE TOTAL SUM BENEATH THE TABLE
    this.totalSumElem.innerHTML = totalSumText(this.totalAmount);
    // MOVE ITEMS FROM LOCAL TO USER ACCOUNT
    if (this.addButton) {
      this.addButton.addEventListener('click', async (e) => {
        const result = await addRowsFromLocalStorageWhenLoggedIn(
          wishlistData,
          this.customerId,
          this.shopDomain,
          this.quantityDecreaseText,
          this.quantityIncreaseText,
          this.quantityInputText,
          this.favContainer,
          this.gridView,
          this.addToCartText,
          this.unavailableText,
        );
        this.numberDisplay.innerHTML = `${result.totalElements} Produkte`;
        this.totalAmount += result.totalSum;
        this.totalSumElem.innerHTML = totalSumText(this.totalAmount);
        // Clear local storage and hide element
        this.localFavElement.style.display = 'none';
        this.addButton.style.display = 'none';
        localStorage.removeItem('wishlist');
        sessionStorage.removeItem('muun-wishlist-cache');
      });
    }
    // HANDLE TRASH, PLUS/MINUS, QUANTITY DECREASE/INCREASE
    this.favContainer.addEventListener('click', (e) => {
      const button = e.target.closest('[data-action]');
      const row = e.target.closest('.wl-element');
      if (!button) return;
      if (!row) return;
      const cleanedId = row.id.split('-').pop();

      switch (button.dataset.action) {
        // ----- TRASH
        case 'remove-favorite':
          const spinner = row.querySelector(`#loading__spinner-trash-${cleanedId}`);
          const trashIcon = row.querySelector(`#trashIcon-${cleanedId}`);

          spinner.classList.remove('hidden');
          trashIcon.classList.add('hidden');

          if (this.isLoggedIn) {
            postWishlist(this.customerId, cleanedId, this.shopDomain, 'DELETE');
          } else {
            let localWishlist = readLocalWishlist();
            localWishlist = localWishlist.filter((item) => item.productId !== cleanedId);
            saveLocalWishlist(localWishlist);
          }
          spinner.classList.add('hidden');

          // remove item from display + remove from total sum
          let { amount, currency } = parseFormattedPrice(row.querySelector('.sum-price-val').innerHTML);
          this.totalAmount -= parseFloat(amount);
          this.totalSumElem.innerHTML = totalSumText(this.totalAmount);
          row.style.display = 'none';
          // subtract 1 from the total amount of elements
          this.numberOfElements -= 1;
          this.numberDisplay.innerHTML = parseNumberOfProducts(this.numberOfElements);
          // Remove cache
          sessionStorage.removeItem('muun-wishlist-cache');
          break;
        // ----- PLUS/MINUS BUTTONS
        case 'plusminus-button':
          let quantity = parseInt(row.querySelector('input.quantity__input').value, 10);
          if (isNaN(quantity) || quantity < 1) {
            console.warn('Invalid quantity, defaulting to 1');
            quantity = 1;
          }
          const sumField = row.querySelector('.sum-price-val');
          const sumFieldGridView = row.querySelector('.wl-cart-grid');
          const priceField = row.querySelector('.wl-price-value');
          const oldSum = parseFormattedPrice(sumField.innerHTML).amount;
          if (quantity && priceField && oldSum) {
            if (this.isLoggedIn) {
              postWishlist(this.customerId, cleanedId, this.shopDomain, 'UPDATE', quantity);
            } else {
              let localWishlist = readLocalWishlist();
              if (Array.isArray(localWishlist)) {
                const updatedWishlist = localWishlist.map((item) => {
                  if (item.productId === cleanedId) {
                    return {
                      ...item,
                      productAmount: quantity,
                    };
                  }
                  return item;
                });
                saveLocalWishlist(updatedWishlist);
              }
            }
            let { amount, currency } = parseFormattedPrice(priceField.innerHTML);

            if (quantity > 0) {
              amount = quantity * amount;
              this.totalAmount -= parseFloat(oldSum);
              this.totalAmount += amount;
            } else {
              amount = '0,00';
            }
            if (sumField) {
              sumField.innerHTML = `${formattedNr(amount)}${currency}`;
            }
            if (sumFieldGridView) {
              sumFieldGridView.innerHTML = `${this.addToCartText} • ${formattedNr(amount)}${currency}`;
            }
            this.totalSumElem.innerHTML = totalSumText(this.totalAmount);
          }
          // Remove cache
          sessionStorage.removeItem('muun-wishlist-cache');
          break;
      }
    });
    // QUANTITY INPUT
    this.favContainer.addEventListener('input', (e) => {
      const inputField = e.target.closest('input.quantity__input');

      if (inputField) {
        const row = inputField.closest('.wl-element');
        const cleanedId = row.id.split('-').pop();

        if (row) {
          let quantity = parseInt(row.querySelector('input.quantity__input').value, 10);
          if (isNaN(quantity) || quantity < 1) {
            console.warn('Invalid quantity, defaulting to 1');
            quantity = 1;
          }
          const sumField = row.querySelector('.sum-price-val');
          const sumFieldGridView = row.querySelector('.wl-cart-grid');
          const priceField = row.querySelector('span.wl-price-value');
          let oldSum = parseFormattedPrice(sumField.innerHTML).amount;

          if (this.isLoggedIn) {
            postWishlist(this.customerId, cleanedId, this.shopDomain, 'UPDATE', quantity);
          } else {
            let localWishlist = readLocalWishlist();
            if (Array.isArray(localWishlist)) {
              const updatedWishlist = localWishlist.map((item) => {
                if (item.productId === cleanedId) {
                  return {
                    ...item,
                    productAmount: quantity,
                  };
                }
                return item;
              });
              saveLocalWishlist(updatedWishlist);
            }
          }

          if (quantity && priceField && oldSum) {
            let { amount, currency } = parseFormattedPrice(priceField.innerHTML);

            if (quantity > 0) {
              amount = quantity * amount;
              this.totalAmount -= oldSum;
              this.totalAmount += amount;
            } else {
              amount = '0,00';
            }
            if (sumField) {
              sumField.innerHTML = `${formattedNr(amount)}${currency}`;
            }
            if (sumFieldGridView) {
              sumFieldGridView.innerHTML = `${this.addToCartText} • ${formattedNr(amount)}${currency}`;
            }
            this.totalSumElem.innerHTML = totalSumText(this.totalAmount);
          }
        }
      }
      // Remove cache
      sessionStorage.removeItem('muun-wishlist-cache');
    });
    // ADD ALL TO CART
    this.querySelector('#allToCart').addEventListener('click', () => {
      addAllWlElemsToCart();
    });
    // DELETE ALL MODAL
    this.querySelector('#deleteAll').addEventListener('click', () => {
      this.modal.classList.remove('hidden');
      this.modal.classList.add('show');
    });
    this.querySelector('#close-modal').addEventListener('click', () => {
      this.modal.classList.remove('show');
      this.modal.classList.add('hidden');
    });
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.modal.classList.remove('show');
        this.modal.classList.add('hidden');
      }
    });
    // DELETE WHOLE WISHLIST
    this.querySelector('#confirmDelete').addEventListener('click', async () => {
      if (this.isLoggedIn) {
        try {
          wishlistData = await getWishlist(customerId, shopDomain);
          wishlistData = wishlistData.data.map((item) => ({
            productId: item.productId,
            productAmount: item.productAmount ?? 1,
          }));
        } catch (err) {
          console.error('Failed to fetch wishlist:', err);
        }
        for (const elem of wishlistData) {
          postWishlist(this.customerId, elem.productId, this.shopDomain, 'DELETE');
        }
      } else {
        localStorage.removeItem('wishlist');
      }
      for (const row of cartItems) {
        row.classList.add('hidden');
      }
      this.totalAmount = 0.0;
      this.totalSumElem.innerHTML = totalSumText(this.totalAmount);
      this.modal.classList.remove('show');
      this.modal.classList.add('hidden');
      // Remove cache
      sessionStorage.removeItem('muun-wishlist-cache');
    });

    // -----
    // SHOW THE CONTAINER AFTER ALL CALCULATIONS ARE FINISHED
    this.belowTableContainer.classList.remove('hidden');
    // -----
  }
}

customElements.define('wishlist-overview', WishlistOverview);
